/**
 * Problem Controller Hook
 * Manages the practice loop: problem generation, answer checking, stats, celebrations
 */

import { useState, useCallback, useRef, useEffect } from 'react'
import { useSettingsStore } from '@stores/settingsStore'
import { useStatsStore } from '@stores/statsStore'
import { useCelebrations } from './useCelebrations'
import {
  generateMultiplicationProblem,
  generateDivisionProblem,
  generateAdditionProblem,
  generateSubtractionProblem,
  generateWordPracticeProblem,
  generateMultipleChoiceOptions,
  generateLettersProblem,
  type ProblemWithOperation,
  type OperationId,
} from '@lib/problemGenerator'
import type { Settings } from '../types'

export interface CurrentProblem extends ProblemWithOperation {
  options?: (number | string)[]
}

export function useProblemController() {
  const settings = useSettingsStore(state => state.settings)
  const stats = useStatsStore(state => state.stats)
  const incrementSolved = useStatsStore(state => state.incrementSolved)
  const updateBestStreak = useStatsStore(state => state.updateBestStreak)

  const { triggerCelebration } = useCelebrations()

  const [currentProblem, setCurrentProblem] = useState<CurrentProblem | null>(null)
  const [currentStreak, setCurrentStreak] = useState(0)
  const [showFeedback, setShowFeedback] = useState(false)
  const [isCorrect, setIsCorrect] = useState(false)
  const [userAnswer, setUserAnswer] = useState('')

  const feedbackTimeoutRef = useRef<number | null>(null)
  const prefetchedProblemRef = useRef<CurrentProblem | null>(null)
  const prefetchedSettingsSignatureRef = useRef<string | null>(null)

  const buildSettingsSignature = useCallback((currentSettings: Settings) => {
    return JSON.stringify({
      practiceMode: currentSettings.practiceMode,
      mode: currentSettings.mode,
      maxValues: currentSettings.maxValues,
      enabledOperations: currentSettings.enabledOperations,
      wordPractice: currentSettings.wordPractice,
      lettersPractice: currentSettings.lettersPractice,
    })
  }, [])

  // Get enabled operations based on settings
  const getEnabledOperations = useCallback(
    (currentSettings: Settings): OperationId[] => {
      const ops: OperationId[] = []

      // Check if letters or word practice is enabled (exclusive modes)
      if (currentSettings.lettersPractice?.enabled) {
        ops.push('letters')
      } else if (currentSettings.wordPractice.enabled) {
        ops.push('wordPractice')
      } else {
        // Add enabled math operations
        if (currentSettings.enabledOperations.multiplication) ops.push('multiplication')
        if (currentSettings.enabledOperations.division) ops.push('division')
        if (currentSettings.enabledOperations.addition) ops.push('addition')
        if (currentSettings.enabledOperations.subtraction) ops.push('subtraction')
      }

      return ops
    },
    []
  )

  // Generate a new problem
  const generateProblem = useCallback(
    (customSettings?: Settings) => {
      const currentSettings = customSettings || settings
      const enabledOps = getEnabledOperations(currentSettings)

      if (enabledOps.length === 0) {
        console.warn('No operations enabled')
        return null
      }

      // Pick random operation
      const operation = enabledOps[Math.floor(Math.random() * enabledOps.length)]

      let problem: ProblemWithOperation

      switch (operation) {
        case 'multiplication':
          problem = generateMultiplicationProblem(currentSettings)
          problem.operationId = 'multiplication'
          break
        case 'division':
          problem = generateDivisionProblem(currentSettings)
          problem.operationId = 'division'
          break
        case 'addition':
          problem = generateAdditionProblem(currentSettings)
          problem.operationId = 'addition'
          break
        case 'subtraction':
          problem = generateSubtractionProblem(currentSettings)
          problem.operationId = 'subtraction'
          break
        case 'wordPractice':
          problem = generateWordPracticeProblem(currentSettings)
          problem.operationId = 'wordPractice'
          break
        case 'letters':
          problem = generateLettersProblem(currentSettings)
          problem.operationId = 'letters'
          break
        default:
          problem = generateMultiplicationProblem(currentSettings)
          problem.operationId = 'multiplication'
      }

      // Generate multiple choice options if in multiple choice mode
      if (currentSettings.mode === 'multipleChoice' && operation !== 'letters') {
        const options = generateMultipleChoiceOptions(problem, currentSettings)
        return { ...problem, options }
      }

      return problem
    },
    [settings, getEnabledOperations]
  )

  // Start a new problem
  const nextProblem = useCallback(() => {
    const settingsSignature = buildSettingsSignature(settings)
    let problem: CurrentProblem | null = null

    if (prefetchedProblemRef.current && prefetchedSettingsSignatureRef.current === settingsSignature) {
      problem = prefetchedProblemRef.current
    }

    if (!problem) {
      problem = generateProblem()
    }

    prefetchedProblemRef.current = null
    prefetchedSettingsSignatureRef.current = null

    setCurrentProblem(problem)
    setShowFeedback(false)
    setIsCorrect(false)
    setUserAnswer('')

    // Clear any pending feedback timeout
    if (feedbackTimeoutRef.current) {
      clearTimeout(feedbackTimeoutRef.current)
      feedbackTimeoutRef.current = null
    }
  }, [buildSettingsSignature, generateProblem, settings])

  // Check the user's answer
  const checkAnswer = useCallback(
    (answer: string | number) => {
      if (!currentProblem) return

      const normalizedAnswer = String(answer).trim().toLowerCase()
      const correctAnswer = String(currentProblem.answer).toLowerCase()

      const correct = normalizedAnswer === correctAnswer

      setIsCorrect(correct)
      setShowFeedback(true)

      if (correct) {
        // Update streak
        const newStreak = currentStreak + 1
        setCurrentStreak(newStreak)
        updateBestStreak(newStreak)

        // Update stats
        incrementSolved()

        // Trigger celebration
        triggerCelebration({
          confettiEnabled: settings.celebrations.confetti,
          streak: newStreak,
        })

        // Auto-advance after a delay
        feedbackTimeoutRef.current = window.setTimeout(() => {
          nextProblem()
        }, 1200)
      } else {
        // Reset streak on wrong answer
        setCurrentStreak(0)

        // Don't auto-advance on wrong answer
      }
    },
    [
      currentProblem,
      currentStreak,
      incrementSolved,
      updateBestStreak,
      triggerCelebration,
      settings.celebrations.confetti,
      nextProblem,
    ]
  )

  // Skip to next problem
  const skipProblem = useCallback(() => {
    setCurrentStreak(0)
    nextProblem()
  }, [nextProblem])

  const prefetchNextProblem = useCallback(() => {
    const problem = generateProblem()
    prefetchedProblemRef.current = problem
    prefetchedSettingsSignatureRef.current = buildSettingsSignature(settings)
    return problem
  }, [buildSettingsSignature, generateProblem, settings])

  // Initialize with first problem
  useEffect(() => {
    if (!currentProblem) {
      nextProblem()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Regenerate problem when settings change
  // Note: We intentionally omit currentProblem and nextProblem from deps
  // to only trigger on settings changes, not on every problem change
  useEffect(() => {
    if (currentProblem) {
      prefetchedProblemRef.current = null
      prefetchedSettingsSignatureRef.current = null
      // Regenerate when mode, practice mode, max values, enabled operations, or difficulty changes
      nextProblem()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    settings.mode,
    settings.practiceMode,
    settings.lettersPractice.enabled,
    settings.lettersPractice.level,
    settings.maxValues.multiplication,
    settings.maxValues.division,
    settings.maxValues.addition,
    settings.maxValues.subtraction,
    settings.enabledOperations.multiplication,
    settings.enabledOperations.division,
    settings.enabledOperations.addition,
    settings.enabledOperations.subtraction,
    settings.wordPractice.difficulty,
  ])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (feedbackTimeoutRef.current) {
        clearTimeout(feedbackTimeoutRef.current)
      }
    }
  }, [])

  return {
    currentProblem,
    currentStreak,
    stats,
    settings,
    showFeedback,
    isCorrect,
    userAnswer,
    setUserAnswer,
    checkAnswer,
    nextProblem,
    skipProblem,
    prefetchNextProblem,
  }
}
